import React, { useState } from 'react';
import { CalendarMonthView } from '../components/calendar/CalendarMonthView';
import { UpcomingEventsList } from '../components/calendar/UpcomingEventsList';
import { getHuecosDeOro } from '../utils/huecosDeOro';
import { ChevronLeft, ChevronRight, Plus, X, Trash2, Calendar as CalendarIcon, Clock, Users, Sparkles, MapPin } from 'lucide-react';
import { BookingModal } from '../components/booking/BookingModal';
import { useDataStore } from '../context/DataStoreContext';
import { CalendarEvent } from '../types/calendar';
export function Calendario() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState<'Month' | 'Week' | 'Day' | 'Agenda'>('Month');
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [selectedDateForBooking, setSelectedDateForBooking] = useState<Date | undefined>(undefined);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const {
    events,
    deleteEvent
  } = useDataStore();
  const huecosDeOro = getHuecosDeOro(currentDate);
  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };
  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };
  const handleToday = () => {
    setCurrentDate(new Date());
  };
  const handleDeleteEvent = () => {
    if (selectedEvent && window.confirm('¿Estás seguro de eliminar este evento?')) {
      deleteEvent(selectedEvent.id);
      setSelectedEvent(null);
    }
  };
  const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  const todayEventsCount = events.filter(e => {
    const d = new Date(e.start);
    const t = new Date();
    return d.getDate() === t.getDate() && d.getMonth() === t.getMonth() && d.getFullYear() === t.getFullYear();
  }).length;
  return <div className="p-4 md:p-8 max-w-[1280px] mx-auto space-y-6 pb-24 md:pb-8 min-h-screen flex flex-col" data-id="element-774">
      {/* Hero header */}
      <section className="relative overflow-hidden rounded-2xl border border-white/[0.06] bg-gradient-to-br from-[#1A1A1A] via-[#141414] to-[#0B0B0B]" data-id="element-775">
        <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-growthly-amber/[0.06] blur-3xl pointer-events-none" data-id="element-776" />
        <div className="relative px-6 md:px-8 py-6 flex flex-col md:flex-row md:items-center justify-between gap-5" data-id="element-777">
          <div data-id="element-778">
            <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-growthly-muted mb-2" data-id="element-779">
              <CalendarIcon size={13} data-id="element-780" />
              <span data-id="element-781">Agenda · {todayEventsCount} hoy</span>
            </div>
            <div className="flex items-center gap-4" data-id="element-782">
              <h1 className="text-[32px] md:text-[40px] font-bold text-white leading-tight tracking-tight" data-id="element-783">
                {monthNames[currentDate.getMonth()]}{' '}
                <span className="text-growthly-muted font-semibold" data-id="element-784">
                  {currentDate.getFullYear()}
                </span>
              </h1>
              <div className="flex items-center bg-white/[0.04] border border-white/[0.08] rounded-lg overflow-hidden" data-id="element-785">
                <button onClick={handlePrevMonth} aria-label="Mes anterior" className="p-2 hover:bg-white/[0.06] transition-colors" data-id="element-786">
                  <ChevronLeft className="w-4 h-4 text-growthly-text" data-id="element-787" />
                </button>
                <button onClick={handleToday} className="px-3 py-2 text-[13px] font-medium text-growthly-text border-x border-white/[0.08] hover:bg-white/[0.06] transition-colors" data-id="element-788">
                  Hoy
                </button>
                <button onClick={handleNextMonth} aria-label="Mes siguiente" className="p-2 hover:bg-white/[0.06] transition-colors" data-id="element-789">
                  <ChevronRight className="w-4 h-4 text-growthly-text" data-id="element-790" />
                </button>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap" data-id="element-791">
            <div className="flex bg-white/[0.04] border border-white/[0.08] rounded-lg p-1" data-id="element-792">
              {(['Month', 'Week', 'Day', 'Agenda'] as const).map(v => <button key={v} onClick={() => setView(v)} className={`px-3 py-1.5 text-[12px] font-medium rounded-md transition-all ${view === v ? 'bg-white text-[#0B0B0B] shadow-sm' : 'text-growthly-muted hover:text-growthly-text'}`} data-id="element-793">
                  {v === 'Month' ? 'Mes' : v === 'Week' ? 'Semana' : v === 'Day' ? 'Día' : 'Agenda'}
                </button>)}
            </div>
            <button onClick={() => {
            setSelectedDateForBooking(new Date());
            setIsBookingOpen(true);
          }} className="inline-flex items-center gap-2 bg-white text-[#0B0B0B] px-4 py-2 rounded-lg text-[13px] font-semibold hover:bg-white/90 transition-colors shadow-lg shadow-black/20" data-id="element-794">
              <Plus className="w-4 h-4" data-id="element-795" />
              Nuevo evento
            </button>
          </div>
        </div>

        {/* Legend */}
        <div className="relative px-6 md:px-8 pb-5 flex items-center gap-5 flex-wrap text-[11px] text-growthly-muted" data-id="element-796">
          <div className="flex items-center gap-1.5" data-id="element-797">
            <span className="w-2 h-2 rounded-full bg-growthly-green" data-id="element-798" />
            Demo
          </div>
          <div className="flex items-center gap-1.5" data-id="element-799">
            <span className="w-2 h-2 rounded-full bg-growthly-amber" data-id="element-800" />
            Propuesta
          </div>
          <div className="flex items-center gap-1.5" data-id="element-801">
            <span className="w-2 h-2 rounded-full bg-white/60" data-id="element-802" />
            Cita
          </div>
          <div className="flex items-center gap-1.5" data-id="element-803">
            <Sparkles size={12} className="text-growthly-green/70" data-id="element-804" />
            <span data-id="element-805">Huecos de Oro</span>
          </div>
        </div>
      </section>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-4 gap-6 min-h-0" data-id="element-806">
        <div className="lg:col-span-3 min-h-[600px]" data-id="element-807">
          <CalendarMonthView events={events} huecosDeOro={huecosDeOro} currentDate={currentDate} onSlotClick={date => {
          setSelectedDateForBooking(date);
          setIsBookingOpen(true);
        }} onEventClick={event => setSelectedEvent(event)} data-id="element-808" />
        </div>
        <div className="hidden lg:block lg:col-span-1 h-full" data-id="element-809">
          <UpcomingEventsList events={events} data-id="element-810" />
        </div>
      </div>

      <BookingModal isOpen={isBookingOpen} onClose={() => setIsBookingOpen(false)} initialDate={selectedDateForBooking} data-id="element-811" />

      {/* Event Details Modal */}
      {selectedEvent && <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-id="element-812">
          <div className="bg-growthly-surface border border-white/10 rounded-2xl shadow-2xl max-w-[440px] w-full overflow-hidden flex flex-col" data-id="element-813">
            <div className="px-6 py-5 border-b border-white/[0.06] flex items-center justify-between bg-gradient-to-br from-white/[0.04] to-transparent" data-id="element-814">
              <div className="flex items-center gap-2" data-id="element-815">
                <div className="w-8 h-8 rounded-lg bg-white/[0.06] flex items-center justify-center" data-id="element-816">
                  <CalendarIcon className="w-4 h-4 text-growthly-text" data-id="element-817" />
                </div>
                <h2 className="text-[17px] font-semibold text-white" data-id="element-818">
                  Detalles del evento
                </h2>
              </div>
              <button onClick={() => setSelectedEvent(null)} className="text-growthly-muted hover:text-growthly-text transition-colors" aria-label="Cerrar" data-id="element-819">
                <X className="w-5 h-5" data-id="element-820" />
              </button>
            </div>

            <div className="p-6 space-y-5" data-id="element-821">
              <div data-id="element-822">
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-growthly-muted mb-2" data-id="element-823">
                  {selectedEvent.type}
                </p>
                <h3 className="text-[20px] font-bold text-white mb-1 leading-tight" data-id="element-824">
                  {selectedEvent.title}
                </h3>
                <p className="text-[13px] text-growthly-muted" data-id="element-825">
                  {selectedEvent.leadName}
                </p>
              </div>

              <div className="space-y-3 bg-white/[0.03] border border-white/[0.06] rounded-xl p-4" data-id="element-826">
                <div className="flex items-center text-[13px] text-growthly-text" data-id="element-827">
                  <CalendarIcon className="w-4 h-4 mr-3 text-growthly-muted flex-shrink-0" data-id="element-828" />
                  <span className="capitalize" data-id="element-829">
                    {new Date(selectedEvent.start).toLocaleDateString('es-ES', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
                  </span>
                </div>
                <div className="flex items-center text-[13px] text-growthly-text" data-id="element-830">
                  <Clock className="w-4 h-4 mr-3 text-growthly-muted flex-shrink-0" data-id="element-831" />
                  {new Date(selectedEvent.start).toLocaleTimeString('es-ES', {
                hour: '2-digit',
                minute: '2-digit'
              })}{' '}
                  -{' '}
                  {new Date(selectedEvent.end).toLocaleTimeString('es-ES', {
                hour: '2-digit',
                minute: '2-digit'
              })}
                </div>
                <div className="flex items-start text-[13px] text-growthly-text" data-id="element-832">
                  <Users className="w-4 h-4 mr-3 text-growthly-muted mt-0.5 flex-shrink-0" data-id="element-833" />
                  <div className="space-y-0.5 min-w-0" data-id="element-834">
                    {selectedEvent.attendees.map((a, i) => <div key={i} className="truncate" data-id="element-835">
                        {a.email}
                      </div>)}
                  </div>
                </div>
              </div>

              {selectedEvent.description && <div data-id="element-836">
                  <h4 className="text-[10px] font-semibold uppercase tracking-[0.14em] text-growthly-muted mb-2" data-id="element-837">
                    Descripción
                  </h4>
                  <p className="text-[13px] text-growthly-text whitespace-pre-wrap leading-relaxed" data-id="element-838">
                    {selectedEvent.description}
                  </p>
                </div>}
            </div>

            <div className="px-6 py-4 border-t border-white/[0.06] bg-white/[0.02] flex justify-between items-center" data-id="element-839">
              <button onClick={handleDeleteEvent} className="flex items-center gap-2 text-[13px] font-medium text-growthly-red hover:text-red-400 transition-colors" data-id="element-840">
                <Trash2 className="w-4 h-4" data-id="element-841" />
                Eliminar
              </button>
              <button onClick={() => setSelectedEvent(null)} className="btn-secondary" data-id="element-842">
                Cerrar
              </button>
            </div>
          </div>
        </div>}
    </div>;
}